import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { toast } from "sonner";
import { ProductOrderService } from '../services/ProductOrderService';
import { createLogger } from '@/lib/logging';
import { restoreDocumentOutcomes } from '@/domain/document-processing/utils/public-document-outcomes';

const logger = createLogger('ProductOrderStore');
let historicalLoad = 0;

interface ProductOrder {
  id: string;
  order_number: string;
  manual_order_number?: string;
  status: 'draft' | 'processing' | 'completed' | 'archived';
  created_at: string;
  created_by: string;
  tenant_id?: string;
  organization_id?: string;
  updated_at: string;
}

type OrderTransition = {
  from: ProductOrder['status'];
  to: ProductOrder['status'];
  allowed: boolean;
};

const VALID_TRANSITIONS: OrderTransition[] = [
  { from: 'draft', to: 'processing', allowed: true },
  { from: 'processing', to: 'completed', allowed: true },
  { from: 'processing', to: 'draft', allowed: true }, // Allow reverting on error
  { from: 'completed', to: 'archived', allowed: true },
  // All other transitions are disallowed
];

interface ProductOrderState {
  currentOrder: ProductOrder | null;
  isLoading: boolean;
  error: string | null;
  viewMode: 'create' | 'edit' | 'view';
  setCurrentOrder: (order: ProductOrder | null, viewMode?: 'create' | 'edit' | 'view') => void;
  createOrder: (userId: string, organizationId: string, token: string | null, manualOrderNumber?: string) => Promise<ProductOrder>;
  loadCurrentOrder: (token: string | null) => Promise<ProductOrder | null>;
  updateOrderStatus: (status: ProductOrder['status'], token: string | null) => Promise<void>;
  canTransitionTo: (status: ProductOrder['status']) => boolean;
  loadOrderById: (orderId: string, silent?: boolean) => Promise<ProductOrder>;
  loadHistoricalOrder: (order: ProductOrder, token: string, isRequested?: () => boolean) => Promise<ProductOrder>;
}

/** The server answered, but with no order: the order ID is stale. */
export class OrderNotFoundError extends Error {
  constructor(orderId: string) {
    super(`Order with ID ${orderId} not found`);
    this.name = 'OrderNotFoundError';
  }
}

export const useProductOrderStore = create<ProductOrderState>((set, get) => ({
  currentOrder: null,
  isLoading: false,
  error: null,
  viewMode: 'create',
  
  setCurrentOrder: (order, viewMode) => {
    set({ currentOrder: order, viewMode: viewMode || (order ? 'view' : 'create') });
  },
  
  canTransitionTo: (targetStatus: ProductOrder['status']) => {
    const currentOrder = get().currentOrder;
    if (!currentOrder) return false;
    
    const transition = VALID_TRANSITIONS.find(
      t => t.from === currentOrder.status && t.to === targetStatus
    );
    
    return transition?.allowed ?? false;
  },
  
  createOrder: async (userId: string, organizationId: string, token: string | null, manualOrderNumber?: string) => {
    if (get().isLoading) throw new Error('Operation in progress');
    
    const existingDraft = get().currentOrder;
    if (existingDraft?.status === 'draft') {
      throw new Error('A draft order already exists');
    }
    
    set({ isLoading: true, error: null });
    try {
      logger.info('Creating new order for user: ' + userId);
      const order = await ProductOrderService.createOrder(userId, organizationId, manualOrderNumber);
      logger.info('Order created successfully', { orderId: order.id });
      set({ currentOrder: order });
      return order;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create order';
      logger.error('Error creating order: ' + message);
      set({ error: message });
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  loadCurrentOrder: async (token: string | null) => {
    if (get().isLoading) return get().currentOrder;
    
    set({ isLoading: true, error: null });
    try {
      const orders = await ProductOrderService.getOrders();
      const currentOrder = orders.find(order => 
        order.status === 'draft' || order.status === 'processing'
      );
      set({ currentOrder });
      return currentOrder;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load order';
      set({ error: message });
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateOrderStatus: async (targetStatus, token) => {
    const currentOrder = get().currentOrder;
    if (!currentOrder) {
      const error = 'No current order to update';
      logger.error(error);
      set({ error });
      throw new Error(error);
    }

    // If we're already in the target state, just return
    if (currentOrder.status === targetStatus) {
      return;
    }

    if (!get().canTransitionTo(targetStatus)) {
      const error = `Invalid status transition from ${currentOrder.status} to ${targetStatus}`;
      logger.error(error);
      set({ error });
      throw new Error(error);
    }

    if (get().isLoading) throw new Error('Operation in progress');
    
    set({ isLoading: true, error: null });
    try {
      logger.info('Updating order status: ' + targetStatus);
      await ProductOrderService.updateOrderStatus(currentOrder.id, targetStatus);
      set((state) => ({
        currentOrder: state.currentOrder
          ? { ...state.currentOrder, status: targetStatus }
          : null
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update order status';
      logger.error('Error updating order status: ' + message);
      set({ error: message });
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  loadOrderById: async (orderId, silent = false) => {
    if (get().isLoading) throw new Error('Operation in progress');

    set({ isLoading: true, error: null });
    try {
      const order = await ProductOrderService.getOrderById(orderId);
      if (!order) {
        throw new OrderNotFoundError(orderId);
      }
      set({ currentOrder: order, viewMode: 'view' });
      return order;
    } catch (error) {
      const message = error instanceof Error ? error.message : `Failed to load order ${orderId}`;
      logger.error('Error loading order: ' + message);
      set({ error: message });
      if (!silent) toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  loadHistoricalOrder: async (order: ProductOrder, token: string, isRequested = () => true) => {
    if (!isRequested()) return order;
    const request = ++historicalLoad;
    const isCurrent = () => request === historicalLoad && isRequested() && get().currentOrder?.id === order.id;
    set({ isLoading: true, error: null, currentOrder: order, viewMode: 'view' });
    const { useDocumentStore, ProcessingStage } = await import('@/stores/useDocumentStore');
    const documentStore = useDocumentStore.getState();
    try {
      if (!isCurrent()) return order;
      documentStore.reset();
      const { OrderHistoryService } = await import('@/domain/order-history/services/OrderHistoryService');
      if (!isCurrent()) return order;
      const snapshotData = await Sentry.startSpan({ name: 'Fetch saved results', op: 'order.open.step' }, () => OrderHistoryService.getOrderEntities(order.id, token));
      // New Order or another order may have been selected while the request ran.
      if (!isCurrent()) return order;
      const snapshot = snapshotData?.entities;
      if (snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)) {
        const restored = restoreDocumentOutcomes(snapshot, order.id);
        documentStore.setConsolidationResult({ ...snapshot, snapshotId: snapshotData.id, consolidatedEntities: snapshot.consolidatedEntities || [], rawEntities: snapshot.rawEntities || [], metadata: snapshot.metadata || { sourceFiles: [], processingTime: 0, timestamp: '' } });
        documentStore.setExtractedEntities(snapshot.consolidatedEntities?.length ? snapshot.consolidatedEntities : restored.results.flatMap(result => result.entities));
        documentStore.setResults(restored.results);
        documentStore.setDocumentMetadata({
          processingStage: restored.stage === 'complete' ? ProcessingStage.COMPLETE
            : restored.stage === 'error' ? ProcessingStage.ERROR : ProcessingStage.IDLE,
          processingErrors: restored.processingErrors,
          fileName: restored.results[0]?.fileName,
          totalFiles: restored.results.length,
          uploadDate: snapshot.metadata?.timestamp ? new Date(snapshot.metadata.timestamp) : undefined
        });
        documentStore.setShowConsolidatedEntities(true);
      } else {
        // A legacy or empty order has no recorded outcome. Do not fabricate success.
        documentStore.setExtractedEntities(Array.isArray(snapshotData) ? snapshotData : []);
        documentStore.setDocumentMetadata({ processingStage: ProcessingStage.IDLE });
      }
      documentStore.setProcessing(false);
      return order;
    } catch (error) {
      if (isCurrent()) {
        const message = 'The saved results could not be loaded. Reopen this order to try again.';
        set({ error: message });
        documentStore.setDocumentMetadata({ processingStage: ProcessingStage.ERROR, processingErrors: [message] });
        documentStore.setProcessing(false);
      }
      throw error;
    } finally {
      if (request === historicalLoad) set({ isLoading: false });
    }
  },
}));
